<template>
  <CheckoutHeader :current-step="1" />
  <div class="relative overflow-hidden md:min-h-[calc(100vh-81px)]">
    <BaseContainer>
      <div ref="container">
        <div
          class="transition-all duration-500 ease-in-out"
          :style="{
            paddingRight: open ? `${openOffset}px` : '0',
          }"
        >
          <CheckoutLayoutHeader
            class="mt-0 md:mt-0"
            :title="$t('checkout.choose_seats')"
          >
            <CheckoutTicketHeader :eventItem="emptyEventItem" />
          </CheckoutLayoutHeader>

          <div
            class="flex justify-center p-6 rounded-md border border-surface-grey w-full"
          >
            <img
              src="/images/seats.png"
              alt="Seat Selection"
              class="max-w-full w-auto h-auto cursor-pointer"
              @click="handleImageClick"
            />
          </div>
        </div>
      </div>
    </BaseContainer>

    <transition name="slidedown">
      <div
        class="fixed md:hidden bottom-0 left-0 right-0 z-20 bg-surface-light border-t border-surface-grey p-4 flex justify-between items-center"
        v-if="!open"
      >
        <div class="absolute left-0 right-0 bottom-full flex justify-center">
          <button
            type="button"
            @click="open = true"
            class="text-sm text-text rounded-t flex mx-auto py-3 px-4 bottom-full bg-surface-light border border-surface-grey cursor-pointer"
          >
            {{ $t('checkout.see_seats', { selected, total }) }}
          </button>
        </div>

        <BaseButton
          class="w-full"
          color="primary"
          size="lg"
          to="/checkout/information"
        >
          {{ $t('globals.continue_to_checkout') }}
          <ArrowRightIcon class="fill-current h-4 w-4 ml-2" />
        </BaseButton>
      </div>
    </transition>

    <transition name="slidein">
      <div
        class="flex flex-col w-full md:max-w-94 fixed md:fixed right-0 top-0 md:top-[81px] bottom-0 shadow-xl overflow-auto bg-surface-light z-50"
        v-if="open"
      >
        <div
          class="flex items-center justify-between sticky top-0 border-b border-surface-grey px-6 py-4 text-lg font-semibold bg-surface-light z-10"
        >
          {{ $t('checkout.your_seats') }}

          <button
            @click="open = false"
            type="button"
            :title="$t('common.close')"
            class="cursor-pointer hover:opacity-50 cursor-pointer"
          >
            <CloseIcon class="h-6 w-6" />
          </button>
        </div>

        <div class="p-6 mb-4 grow overflow-y-auto">
          <div class="rounded-md bg-surface-grey-light p-4">
            <div class="flex items-center gap-4 mb-2">
              <div class="text-xs text-text uppercase">
                {{ $t('checkout.seats') }}
              </div>
              <div class="h-px bg-surface-grey flex-1"></div>
            </div>

            <div
              class="flex w-full justify-between mb-2"
              v-for="i in 2"
              :key="i"
            >
              <div>
                <div class="font-semibold">Række 19 · Sæde 10</div>
                <div class="text-xs text-text">Gulv</div>
              </div>

              <div class="font-semibold">
                {{ formatPrice(450, 'DKK', $i18n.locale) }}
              </div>
            </div>
          </div>
        </div>

        <div class="p-6 border-t border-surface-grey">
          <CartSummaryTable />
          <BaseButton
            class="w-full mt-4"
            color="primary"
            size="lg"
            to="/checkout/information"
          >
            {{ $t('globals.continue_to_checkout') }}
            <ArrowRightIcon class="fill-current h-4 w-4 ml-2" />
          </BaseButton>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
  import { useHead } from '@unhead/vue';
  import { onBeforeUnmount, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  import CheckoutHeader from '@/components/checkout/CheckoutHeader.vue';
  import CheckoutLayoutHeader from '@/components/checkout/CheckoutLayoutHeader.vue';
  import CartSummaryTable from '@/components/checkout/CheckoutSummaryTable.vue';
  import CheckoutTicketHeader from '@/components/checkout/CheckoutTicketHeader.vue';
  import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue';
  import CloseIcon from '@/components/icons/CloseIcon.vue';
  import BaseButton from '@/components/ui/BaseButton.vue';
  import BaseContainer from '@/components/ui/BaseContainer.vue';
  import { useCartStore } from '@/stores/cart';
  import { formatPrice } from '@/utils/formatPrice';
  import { emptyEventItem } from '@/data';

  const { t } = useI18n();

  const { items } = useCartStore();

  useHead({
    title: t('checkout.choose_seats'),
    meta: [
      {
        property: 'og:title',
        content: t('checkout.choose_seats'),
      },
    ],
  });

  const open = ref(false);

  const openOffset = ref(0);

  const selected = ref(0);

  const total = items[0]?.tickets[0]?.count || 0;

  const container = ref<HTMLElement | null>(null);

  const calculateOffset = () => {
    if (!container.value || window.innerWidth < 992) {
      openOffset.value = 0;
      return;
    }

    const rect = container.value.getBoundingClientRect();
    openOffset.value = Math.max(
      375 + 24 - (window.innerWidth - rect.width) / 2,
      0
    );
  };

  const handleImageClick = () => {
    selected.value = selected.value + 1;

    if (window.innerWidth < 992) {
      if (selected.value >= total) {
        open.value = true;
      }

      return;
    }

    open.value = true;
  };

  onMounted(() => {
    calculateOffset();
    window.addEventListener('resize', calculateOffset);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', calculateOffset);
  });
</script>
